export const DEFAULT_CURRENCY = "USD";
export const DEFAULT_CURRENCY_NUMERIC = "840";

/** ISO 4217 numeric code -> alphabetic (Intl expects alphabetic). */
const NUMERIC_TO_ALPHA_CURRENCY: Record<string, string> = {
  "840": "USD",
  "978": "EUR",
  "826": "GBP",
  "392": "JPY",
  "756": "CHF",
  "124": "CAD",
  "036": "AUD",
};

const ALPHA_TO_NUMERIC_CURRENCY: Record<string, string> = Object.fromEntries(
  Object.entries(NUMERIC_TO_ALPHA_CURRENCY).map(([numeric, alpha]) => [alpha, numeric]),
);

export function resolveCurrencyCode(currency?: string | number | null): string {
  if (currency == null) return DEFAULT_CURRENCY;

  const raw = String(currency).trim();
  if (!raw) return DEFAULT_CURRENCY;

  const fromNumeric = NUMERIC_TO_ALPHA_CURRENCY[raw];
  if (fromNumeric) return fromNumeric;

  if (raw.length === 3 && /^[A-Za-z]{3}$/.test(raw)) {
    return raw.toUpperCase();
  }

  return DEFAULT_CURRENCY;
}

/** Resolves tenant/API currency to ISO 4217 numeric code for backend requests. */
export function resolveCurrencyNumericCode(currency?: string | number | null): string {
  if (currency == null) return DEFAULT_CURRENCY_NUMERIC;

  const raw = String(currency).trim();
  if (!raw) return DEFAULT_CURRENCY_NUMERIC;

  if (NUMERIC_TO_ALPHA_CURRENCY[raw]) return raw;

  if (raw.length === 3 && /^[A-Za-z]{3}$/.test(raw)) {
    return ALPHA_TO_NUMERIC_CURRENCY[raw.toUpperCase()] ?? DEFAULT_CURRENCY_NUMERIC;
  }

  return DEFAULT_CURRENCY_NUMERIC;
}

/** ISO 4217 numeric code for every outgoing API `currency` field. */
export function toRequestCurrency(currency?: string | number | null): string {
  return resolveCurrencyNumericCode(currency);
}

export interface FormatCurrencyOptions {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  locale?: string;
}

export function formatCurrencyAmount(
  amount: number,
  currency?: string | number | null,
  options?: FormatCurrencyOptions,
): string {
  const code = resolveCurrencyCode(currency);
  const minimumFractionDigits = options?.minimumFractionDigits ?? 2;
  const maximumFractionDigits = options?.maximumFractionDigits ?? 2;

  return new Intl.NumberFormat(options?.locale ?? "en-US", {
    style: "currency",
    currency: code,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);
}

/** Variable decimals - public marketing pages (tuition, fees). */
export function formatCurrencyCompact(
  amount: number,
  currency?: string | number | null,
): string {
  return formatCurrencyAmount(amount, currency, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/** Fixed cents - balance cards, revenue summaries. */
export function formatBalanceAmount(
  amount: number,
  currency?: string | number | null,
): string {
  return formatCurrencyAmount(amount, currency);
}

/**
 * Signed ledger balance: `-$250.00` owes, `$0.00` paid up, `+$250.00` credit.
 * Uses the absolute formatted amount so the sign is always ASCII `+` / `-`.
 */
export function formatSignedBalanceAmount(
  amount: number,
  currency?: string | number | null,
): string {
  const formatted = formatBalanceAmount(Math.abs(amount), currency);
  if (amount > 0) return `+${formatted}`;
  if (amount < 0) return `-${formatted}`;
  return formatted;
}

export function currencyFieldLabel(
  base: string,
  currency?: string | number | null,
): string {
  return `${base} (${resolveCurrencyCode(currency)})`;
}

/** @deprecated Use formatCurrencyCompact with tenant currency. */
export function formatUsd(amount: number): string {
  return formatCurrencyCompact(amount, DEFAULT_CURRENCY);
}

/** @deprecated Use formatCurrencyAmount with tenant currency. */
export function formatUsdAmount(amount: number): string {
  return formatCurrencyAmount(amount, DEFAULT_CURRENCY);
}

export function formatCurrencyAmountWithCode(
  amount: number,
  currency?: string | number | null,
): string {
  const code = resolveCurrencyCode(currency);
  return `${formatCurrencyAmount(amount, code)} ${code}`;
}

/** @deprecated Use formatCurrencyAmountWithCode with tenant currency. */
export function formatUsdAmountWithCode(amount: number): string {
  return formatCurrencyAmountWithCode(amount, DEFAULT_CURRENCY);
}

export function splitCurrencyAmountForDisplay(
  amount: number,
  currency?: string | number | null,
): {
  main: string;
  cents: string;
} {
  const formatted = formatCurrencyAmount(amount, currency);
  const dotIndex = formatted.lastIndexOf(".");

  if (dotIndex === -1 || formatted.length - dotIndex !== 3) {
    return { main: formatted, cents: "" };
  }

  return {
    main: formatted.slice(0, dotIndex),
    cents: formatted.slice(dotIndex),
  };
}

/** @deprecated Use splitCurrencyAmountForDisplay with tenant currency. */
export function splitUsdAmountForDisplay(amount: number): {
  main: string;
  cents: string;
} {
  return splitCurrencyAmountForDisplay(amount, DEFAULT_CURRENCY);
}
